import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  ButtonGroup,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { updateLeadStatus, setLeads, addLead, deleteLead } from '../redux/slices/leadSlice';
import * as mockDataService from '../services/mockData';
import * as api from '../services/api';
import LeadImporter from '../components/LeadImporter';
import LeadTooltip from '../components/LeadTooltip';
import LeadStatusIndicator from '../components/LeadStatusIndicator';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Lead } from '../types/lead';
import { useCreateNotification } from '../hooks/useCreateNotification';
import { NOTIFICATION_TYPES } from '../utils/notificationUtils';

const statusColors: Record<string, "info" | "warning" | "primary" | "success" | "error"> = {
  "Novo": 'info',
  "Contatado": 'warning',
  "Qualificado": 'primary',
  "Convertido": 'success',
  "Perdido": 'error',
};

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  source: string;
  status: string;
  notes: string;
  productInterest: string;
}

const LeadManagementPage: React.FC = () => {
  const dispatch = useDispatch();
  const createNotification = useCreateNotification();
  const leads = useSelector((state: RootState) => state.leads.items);
  const products = useSelector((state: RootState) => state.products.items);
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    source: 'Site',
    status: 'Novo',
    notes: '',
    productInterest: '',
  });

  useEffect(() => {
    loadLeads();
  }, [dispatch]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      
      // Carregar leads do MongoDB via API
      let apiLeads: Lead[] = [];
      try {
        const apiResponse = await api.getLeads();
        apiLeads = apiResponse.data;
      } catch (apiError) {
        console.error('Erro ao carregar leads da API:', apiError);
      }
      
      // Carregar leads do localStorage
      const localLeads = await mockDataService.getLeads();
      
      // Mesclar leads, evitando duplicatas por ID ou número de telefone
      const mergedLeads: Lead[] = [...apiLeads];
      
      // Adicionar leads do localStorage que não existem na API
      localLeads.forEach((localLead: Lead) => {
        // Verificar se o lead já existe na lista mesclada
        const exists = mergedLeads.some(l => 
          l.id === localLead.id || 
          (l.phone === localLead.phone && localLead.phone) ||
          (l.email === localLead.email && localLead.email && l.email)
        );
        
        if (!exists) {
          mergedLeads.push(localLead);
        }
      });
      
      dispatch(setLeads(mergedLeads));
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
      createNotification({
        type: NOTIFICATION_TYPES.LEAD.ERROR.type,
        title: 'Erro ao carregar leads',
        message: 'Erro ao carregar leads. Tente novamente mais tarde.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (leadId: string) => (event: SelectChangeEvent) => {
    try {
      const newStatus = event.target.value;
      
      // Atualiza o status no Redux
      dispatch(updateLeadStatus({
        id: leadId,
        status: newStatus
      }));
      
      // Atualiza a data de último contato separadamente
      
      // Atualiza o status no localStorage
      mockDataService.updateLead(leadId, { 
        status: newStatus,
        lastContactDate: new Date().toISOString()
      });
      
      createNotification({
        type: NOTIFICATION_TYPES.LEAD.UPDATED.type,
        title: 'Lead atualizado',
        message: `Status do lead atualizado para ${newStatus}`
      });
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      createNotification({
        type: NOTIFICATION_TYPES.LEAD.ERROR.type,
        title: 'Erro ao atualizar lead',
        message: 'Erro ao atualizar status do lead'
      });
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleCloseAddDialog = () => setOpenAddDialog(false);
  const handleCloseEditDialog = () => setOpenEditDialog(false);
  const handleCloseImportDialog = () => setOpenImportDialog(false);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSourceChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      source: event.target.value
    });
  };

  const handleStatusFormChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      status: event.target.value
    });
  };

  const handleProductInterestChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      productInterest: event.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Criar novo lead com dados do formulário
      const newLead: Omit<Lead, 'id'> = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        source: formData.source,
        status: formData.status,
        notes: formData.notes,
        productInterest: formData.productInterest || undefined,
        lastContactDate: new Date().toISOString(),
        interactions: [],
        uniqueCode: Math.random().toString(36).substr(2, 8).toUpperCase(),
      };
      
      // Adicionar lead ao localStorage
      const addedLead = await mockDataService.createLead(newLead);
      
      if (addedLead) {
        // Adicionar lead ao Redux
        dispatch(addLead(addedLead));
        
        createNotification({
          type: NOTIFICATION_TYPES.LEAD.CREATED.type,
          title: 'Lead adicionado',
          message: `Lead ${addedLead.name} adicionado com sucesso!`
        });
        
        // Resetar formulário e fechar diálogo
        setFormData({
          name: '',
          email: '',
          phone: '',
          source: 'Site',
          status: 'Novo',
          notes: '',
          productInterest: '',
        });
        
        handleCloseAddDialog();
      }
    } catch (error) {
      console.error('Erro ao adicionar lead:', error);
      createNotification({
        type: NOTIFICATION_TYPES.LEAD.ERROR.type,
        title: 'Erro ao adicionar lead',
        message: 'Erro ao adicionar lead. Tente novamente.'
      });
    }
  };
  
  const handleEditClick = (leadId: string) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setSelectedLeadId(leadId);
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        source: lead.source || 'Site',
        status: lead.status || 'Novo',
        notes: lead.notes || '',
        productInterest: lead.productInterest || '',
      });
      setOpenEditDialog(true);
    }
  };
  
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLeadId) return;
    
    try {
      // Atualizar lead com dados do formulário
      const updatedLead = {
        id: selectedLeadId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        source: formData.source,
        status: formData.status,
        notes: formData.notes,
        productInterest: formData.productInterest || undefined,
        lastContactDate: new Date().toISOString(),
      };
      
      // Atualizar lead no localStorage
      await mockDataService.updateLead(selectedLeadId, updatedLead);
      
      // Atualizar lead no Redux
      const currentLead = leads.find(l => l.id === selectedLeadId);
      if (currentLead) {
        const updatedLeadWithAllProps = {
          ...currentLead,
          ...updatedLead
        };
        
        // Atualizar o lead no estado global
        dispatch(updateLeadStatus({
          id: selectedLeadId,
          status: formData.status
        }));
        
        // Recarregar os leads para atualizar todas as propriedades
        loadLeads();
        
        createNotification({
          type: NOTIFICATION_TYPES.LEAD.UPDATED.type,
          title: 'Lead atualizado',
          message: `Lead ${formData.name} atualizado com sucesso!`
        });
        
        // Resetar formulário e fechar diálogo
        setFormData({
          name: '',
          email: '',
          phone: '',
          source: 'Site',
          status: 'Novo',
          notes: '',
          productInterest: '',
        });
        
        setSelectedLeadId(null);
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error('Erro ao atualizar lead:', error);
      createNotification({
        type: NOTIFICATION_TYPES.LEAD.ERROR.type,
        title: 'Erro ao atualizar lead',
        message: 'Erro ao atualizar lead. Tente novamente.'
      });
    }
  };

  const handleDeleteLead = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este lead?')) {
      try {
        // Excluir lead do localStorage
        const success = await mockDataService.deleteLead(id);
        
        if (success) {
          // Excluir lead do Redux
          dispatch(deleteLead(id));
          
          createNotification({
            type: NOTIFICATION_TYPES.LEAD.DELETED.type,
            title: 'Lead excluído',
            message: 'Lead excluído com sucesso!'
          });
        }
      } catch (error) {
        console.error('Erro ao excluir lead:', error);
        createNotification({
          type: NOTIFICATION_TYPES.LEAD.ERROR.type,
          title: 'Erro ao excluir lead',
          message: 'Erro ao excluir lead. Tente novamente.'
        });
      }
    }
  };

  const getProductLink = (productId: string) => {
    if (!productId || productId === 'automatic_later') {
      return null;
    }
    
    const product = products.find(p => p.id === productId);
    
    if (!product) {
      return null;
    }
    
    return (
      <Link to={`/produtos/${product.id}`} style={{ textDecoration: 'none' }}>
        <Chip 
          label={product.name} 
          size="small" 
          color="primary" 
          variant="outlined" 
          clickable
        />
      </Link>
    );
  };

  // Função para exportar leads para Excel
  const exportLeadsToExcel = () => {
    try {
      // Preparar dados para exportação
      const exportData = leads.map(lead => ({
        'Nome': lead.name,
        'Email': lead.email,
        'Telefone': lead.phone || '-',
        'Origem': lead.source,
        'Status': lead.status,
        'Data Último Contato': formatDate(lead.lastContactDate),
        'Observações': lead.notes || '-',
        'Produto de Interesse': lead.productInterest ? 
          products.find(p => p.id === lead.productInterest)?.name || '-' : 
          '-',
        'Código Único': lead.uniqueCode || '-'
      }));
      
      // Criar planilha
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');
      
      // Ajustar largura das colunas
      const maxWidth = exportData.reduce(
        (acc, lead) => ({
          'Nome': Math.max(acc['Nome'], lead['Nome'].length),
          'Email': Math.max(acc['Email'], lead['Email'].length),
          'Telefone': Math.max(acc['Telefone'], lead['Telefone'].length),
          'Origem': Math.max(acc['Origem'], lead['Origem'].length),
          'Status': Math.max(acc['Status'], lead['Status'].length),
          'Data Último Contato': Math.max(acc['Data Último Contato'], lead['Data Último Contato'].length),
          'Observações': Math.max(acc['Observações'], Math.min(50, lead['Observações'].length)),
          'Produto de Interesse': Math.max(acc['Produto de Interesse'], lead['Produto de Interesse'].length),
          'Código Único': Math.max(acc['Código Único'], lead['Código Único'].length)
        }),
        { 'Nome': 4, 'Email': 5, 'Telefone': 8, 'Origem': 6, 'Status': 6, 'Data Último Contato': 18, 'Observações': 11, 'Produto de Interesse': 19, 'Código Único': 12 }
      );
      
      // Gerar arquivo
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Salvar arquivo
      saveAs(data, `leads_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      createNotification({
        type: NOTIFICATION_TYPES.LEAD.CREATED.type, // Usando um tipo existente como alternativa
        title: 'Exportação concluída',
        message: `${leads.length} leads exportados com sucesso!`
      });
    } catch (error) {
      console.error('Erro ao exportar leads:', error);
      createNotification({
        type: NOTIFICATION_TYPES.LEAD.ERROR.type, // Usando um tipo existente como alternativa
        title: 'Erro na exportação',
        message: 'Erro ao exportar leads. Tente novamente.'
      });
    }
  };

  // Função para converter status do lead para o tipo da sinaleira
  const convertLeadStatus = (status: string): 'active' | 'warning' | 'critical' => {
    const daysSinceLastContact = (lastContactDate: string) => {
      const lastContact = new Date(lastContactDate);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - lastContact.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };
    
    if (status === 'Convertido') return 'active';
    if (status === 'Perdido') return 'critical';
    
    // Para os outros status, verificar a data do último contato
    const lead = leads.find(l => l.status === status);
    if (lead && lead.lastContactDate) {
      const days = daysSinceLastContact(lead.lastContactDate);
      if (days <= 3) return 'active';
      if (days <= 7) return 'warning';
      return 'critical';
    }
    
    return 'warning';
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        {/* Cabeçalho */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1" sx={{ 
            fontWeight: 'bold', 
            color: 'primary.main',
            letterSpacing: '0.5px',
            padding: '8px 0'
          }}>
            Gerenciamento de Leads
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
                background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                color: 'white',
                fontWeight: 'bold',
                padding: '10px 20px',
                transition: 'all 0.3s',
                '&:hover': { 
                  transform: 'translateY(-3px)', 
                  boxShadow: '0 6px 15px rgba(76, 175, 80, 0.4)',
                  background: 'linear-gradient(45deg, #43A047 30%, #7CB342 90%)'
                }
              }}
            >
              Novo Lead
            </Button>
            
            <Button
              variant="contained"
              startIcon={<FileUploadIcon />}
              onClick={() => setOpenImportDialog(true)}
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
                background: 'linear-gradient(45deg, #2196F3 30%, #03A9F4 90%)',
                color: 'white',
                fontWeight: 'bold',
                padding: '10px 20px',
                transition: 'all 0.3s',
                '&:hover': { 
                  transform: 'translateY(-3px)', 
                  boxShadow: '0 6px 15px rgba(33, 150, 243, 0.4)',
                  background: 'linear-gradient(45deg, #1E88E5 30%, #039BE5 90%)'
                }
              }}
            >
              Importar
            </Button>

            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={exportLeadsToExcel}
              disabled={leads.length === 0}
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 10px rgba(156, 39, 176, 0.3)',
                background: 'linear-gradient(45deg, #9C27B0 30%, #E040FB 90%)',
                color: 'white',
                fontWeight: 'bold',
                padding: '10px 20px',
                transition: 'all 0.3s',
                '&:hover': { 
                  transform: 'translateY(-3px)', 
                  boxShadow: '0 6px 15px rgba(156, 39, 176, 0.4)',
                  background: 'linear-gradient(45deg, #8E24AA 30%, #D500F9 90%)'
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(45deg, #9E9E9E 30%, #BDBDBD 90%)',
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              }}
            >
              Exportar
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : leads.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum lead encontrado
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Adicione novos leads clicando no botão "Novo Lead" ou importe leads existentes.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={{ mt: 2 }}
            >
              Adicionar Lead
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Contato</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>Último Contato</TableCell>
                  <TableCell>Produto</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LeadStatusIndicator status={convertLeadStatus(lead.status)} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Button 
                            size="small" 
                            variant={lead.status === 'Novo' ? 'contained' : 'outlined'}
                            color="info"
                            onClick={() => handleStatusChange(lead.id)({ target: { value: 'Novo' } } as SelectChangeEvent)}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1, 
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: lead.status === 'Novo' ? 'bold' : 'normal',
                              opacity: lead.status === 'Novo' ? 1 : 0.7,
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            Novo
                          </Button>
                          <Button 
                            size="small" 
                            variant={lead.status === 'Contatado' ? 'contained' : 'outlined'}
                            color="warning"
                            onClick={() => handleStatusChange(lead.id)({ target: { value: 'Contatado' } } as SelectChangeEvent)}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1, 
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: lead.status === 'Contatado' ? 'bold' : 'normal',
                              opacity: lead.status === 'Contatado' ? 1 : 0.7,
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            Contatado
                          </Button>
                          <Button 
                            size="small" 
                            variant={lead.status === 'Qualificado' ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => handleStatusChange(lead.id)({ target: { value: 'Qualificado' } } as SelectChangeEvent)}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1, 
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: lead.status === 'Qualificado' ? 'bold' : 'normal',
                              opacity: lead.status === 'Qualificado' ? 1 : 0.7,
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            Qualificado
                          </Button>
                          <Button 
                            size="small" 
                            variant={lead.status === 'Convertido' ? 'contained' : 'outlined'}
                            color="success"
                            onClick={() => handleStatusChange(lead.id)({ target: { value: 'Convertido' } } as SelectChangeEvent)}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1, 
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: lead.status === 'Convertido' ? 'bold' : 'normal',
                              opacity: lead.status === 'Convertido' ? 1 : 0.7,
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            Convertido
                          </Button>
                          <Button 
                            size="small" 
                            variant={lead.status === 'Perdido' ? 'contained' : 'outlined'}
                            color="error"
                            onClick={() => handleStatusChange(lead.id)({ target: { value: 'Perdido' } } as SelectChangeEvent)}
                            sx={{ 
                              minWidth: 'auto', 
                              px: 1, 
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: lead.status === 'Perdido' ? 'bold' : 'normal',
                              opacity: lead.status === 'Perdido' ? 1 : 0.7,
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            Perdido
                          </Button>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <LeadTooltip lead={lead}>
                        <Link to={`/leads/${lead.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <Typography variant="body1" fontWeight="medium">
                            {lead.name}
                          </Typography>
                        </Link>
                      </LeadTooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{lead.email}</Typography>
                      {lead.phone && (
                        <Typography variant="body2" color="text.secondary">
                          {lead.phone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={lead.source} 
                        size="small" 
                        color="default" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(lead.lastContactDate)}</TableCell>
                    <TableCell>
                      {lead.productInterest ? (
                        getProductLink(lead.productInterest)
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        component={Link} 
                        to={`/leads/${lead.id}`}
                        size="small" 
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="info"
                        onClick={() => handleEditClick(lead.id)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteLead(lead.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Dialog para adicionar novo lead */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Adicionar Novo Lead</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="name"
                  label="Nome Completo"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="phone"
                  label="Telefone"
                  fullWidth
                  value={formData.phone}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="source-label">Origem</InputLabel>
                  <Select
                    labelId="source-label"
                    name="source"
                    value={formData.source}
                    onChange={handleSourceChange}
                    label="Origem"
                  >
                    <MenuItem value="Site">Site</MenuItem>
                    <MenuItem value="Facebook">Facebook</MenuItem>
                    <MenuItem value="Instagram">Instagram</MenuItem>
                    <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                    <MenuItem value="Twitter">Twitter</MenuItem>
                    <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                    <MenuItem value="Indicação">Indicação</MenuItem>
                    <MenuItem value="Outro">Outro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    name="status"
                    value={formData.status}
                    onChange={handleStatusFormChange}
                    label="Status"
                  >
                    <MenuItem value="Novo">Novo</MenuItem>
                    <MenuItem value="Contatado">Contatado</MenuItem>
                    <MenuItem value="Qualificado">Qualificado</MenuItem>
                    <MenuItem value="Convertido">Convertido</MenuItem>
                    <MenuItem value="Perdido">Perdido</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="product-interest-label">Produto de Interesse</InputLabel>
                  <Select
                    labelId="product-interest-label"
                    name="productInterest"
                    value={formData.productInterest}
                    onChange={handleProductInterestChange}
                    label="Produto de Interesse"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    <MenuItem value="automatic_later">
                      <em>Automático, vamos falar mais tarde</em>
                    </MenuItem>
                    {products.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name} - R$ {product.price.toFixed(2)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Selecione o produto ou serviço de interesse</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  name="notes"
                  label="Observações"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={handleTextChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">
              Adicionar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog para editar lead */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleEditSubmit}>
          <DialogTitle>Editar Lead</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="name"
                  label="Nome Completo"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="email"
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  name="phone"
                  label="Telefone"
                  fullWidth
                  value={formData.phone}
                  onChange={handleTextChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="source-label-edit">Origem</InputLabel>
                  <Select
                    labelId="source-label-edit"
                    name="source"
                    value={formData.source}
                    onChange={handleSourceChange}
                    label="Origem"
                  >
                    <MenuItem value="Site">Site</MenuItem>
                    <MenuItem value="Facebook">Facebook</MenuItem>
                    <MenuItem value="Instagram">Instagram</MenuItem>
                    <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                    <MenuItem value="Twitter">Twitter</MenuItem>
                    <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                    <MenuItem value="Indicação">Indicação</MenuItem>
                    <MenuItem value="Outro">Outro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="status-label-edit">Status</InputLabel>
                  <Select
                    labelId="status-label-edit"
                    name="status"
                    value={formData.status}
                    onChange={handleStatusFormChange}
                    label="Status"
                  >
                    <MenuItem value="Novo">Novo</MenuItem>
                    <MenuItem value="Contatado">Contatado</MenuItem>
                    <MenuItem value="Qualificado">Qualificado</MenuItem>
                    <MenuItem value="Convertido">Convertido</MenuItem>
                    <MenuItem value="Perdido">Perdido</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="product-interest-label-edit">Produto de Interesse</InputLabel>
                  <Select
                    labelId="product-interest-label-edit"
                    name="productInterest"
                    value={formData.productInterest}
                    onChange={handleProductInterestChange}
                    label="Produto de Interesse"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    <MenuItem value="automatic_later">
                      <em>Automático, vamos falar mais tarde</em>
                    </MenuItem>
                    {products.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name} - R$ {product.price.toFixed(2)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Selecione o produto ou serviço de interesse</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  name="notes"
                  label="Observações"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={handleTextChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">
              Atualizar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Componente de importação de leads */}
      <LeadImporter 
        open={openImportDialog} 
        onClose={handleCloseImportDialog} 
        products={products}
      />
    </Container>
  );
};

export default LeadManagementPage;
